/*
  NUDIENT: Annonsbandets växling, M-139. Laddas av
  sections/nudient-announcement-bar.liquid när bandet har meddelanden.

  - Med flera meddelanden visas ett i taget, och de växlar automatiskt efter
    data-speed sekunder, sektionens inställning, förvalt 5.
  - Växlingen stannar vid hovring, när något i bandet har fokus, när fliken är
    dold och för den som valt minskade rörelser, och fortsätter efteråt, utom
    vid minskade rörelser.
  - Pilarna visar föregående och nästa meddelande. Svep i sidled på pekskärm
    gör samma sak. Efter pilarna och svepet läser skärmläsare upp det nya
    meddelandet, aria-live polite. Den automatiska växlingen läses inte upp.
  - Med ett meddelande gör skriptet ingenting.

  Värden som inte är tokens:
  - 1000: millisekunder per sekund.
  - 40: pixlar i sidled som räknas som ett svep, mer än i höjdled.
  - off och polite i aria-live: tyst vid automatisk växling, uppläst efter
    kundens eget val.
  - (prefers-reduced-motion: reduce): webbläsarens fråga om minskade rörelser.
*/

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const SWIPE = 40;

class NudientAnnouncements extends HTMLElement {
  #index = 0;
  #timer = 0;
  #start = /** @type {{ x: number, y: number } | null} */ (null);

  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.track = this.querySelector('[data-track]');
    if (this.slides.length < 2) return;

    this.querySelector('[data-previous]')?.addEventListener('click', () => this.#step(-1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#step(1, true));
    for (const type of ['mouseenter', 'focusin']) this.addEventListener(type, this.#stop);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#resume();
    });
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    document.addEventListener('visibilitychange', this.#onVisibility);
    reducedMotion.addEventListener('change', this.#resume);
    this.#play();
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    reducedMotion.removeEventListener('change', this.#resume);
  }

  get #speed() {
    const seconds = Number(this.dataset.speed);
    return (Number.isFinite(seconds) && seconds > 0 ? seconds : 5) * 1000;
  }

  /** @param {number} direction @param {boolean} [announce] */
  #step(direction, announce = false) {
    const count = this.slides.length;
    this.#index = (this.#index + direction + count) % count;
    this.slides.forEach((slide, i) => (slide.hidden = i !== this.#index));
    this.track?.setAttribute('aria-live', announce ? 'polite' : 'off');
  }

  #play() {
    this.#stop();
    if (reducedMotion.matches || document.hidden) return;
    if (this.matches(':hover') || this.matches(':focus-within')) return;
    this.#timer = window.setInterval(() => this.#step(1), this.#speed);
  }

  #stop = () => {
    window.clearInterval(this.#timer);
    this.#timer = 0;
  };

  #resume = () => this.#play();

  #onVisibility = () => (document.hidden ? this.#stop() : this.#play());

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#start = event.pointerType === 'mouse' ? null : { x: event.clientX, y: event.clientY };
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#start) return;
    const dx = event.clientX - this.#start.x;
    const dy = event.clientY - this.#start.y;
    this.#start = null;
    if (Math.abs(dx) < SWIPE || Math.abs(dx) < Math.abs(dy)) return;
    this.#step(dx < 0 ? 1 : -1, true);
    this.#play();
  };
}

if (!customElements.get('nudient-announcements')) customElements.define('nudient-announcements', NudientAnnouncements);
